/**
 * templateFilter — search and filtering for the parent console's chore-template
 * library.
 *
 * The library replaced a flat, unsearchable list of every live template. With a
 * household running a couple of dozen templates across chores and homework,
 * finding the one you want was a scroll-and-squint exercise. Kept as pure
 * functions so the matching rules are unit-testable without a render harness.
 */
import type { ChoreTemplateRow } from './useChoreTemplates';

export type KindFilter = 'all' | 'chore' | 'homework';

export interface TemplateFilterOpts {
  /** Free-text query; matched case-insensitively against title, subject and category. */
  query?: string;
  kind?: KindFilter;
  /** Restrict to templates assigned to this member; null/undefined = any. */
  assigneeId?: string | null;
}

/** Case- and diacritic-tolerant contains. Norwegian titles are full of æøå. */
function matches(haystack: string | null | undefined, needle: string): boolean {
  if (!haystack) return false;
  return haystack.toLocaleLowerCase('nb-NO').includes(needle);
}

/**
 * Apply search + kind + assignee. Input order is preserved.
 *
 * An empty query matches everything (rather than nothing), so clearing the box
 * restores the full list without a separate reset control.
 */
export function filterTemplates(
  rows: ChoreTemplateRow[],
  { query, kind = 'all', assigneeId }: TemplateFilterOpts = {},
): ChoreTemplateRow[] {
  const q = (query ?? '').trim().toLocaleLowerCase('nb-NO');
  return rows.filter((r) => {
    // A missing `kind` is a pre-Fase-4 row, which is an ordinary chore.
    if (kind !== 'all' && (r.kind ?? 'chore') !== kind) return false;

    if (assigneeId) {
      // No assignees means an open template — available to everyone, so it
      // belongs in a per-member view rather than being filtered out.
      const ids = r.assignee_ids ?? [];
      if (ids.length > 0 && !ids.includes(assigneeId)) return false;
    }

    if (q.length === 0) return true;
    return (
      matches(r.title, q) ||
      matches(r.subject, q) ||
      matches(r.category, q) ||
      matches(r.description, q)
    );
  });
}
